import {Dispatch, SetStateAction, useLayoutEffect} from 'react';
import {useNodesInitialized} from 'reactflow';

import {unhideFlowchartElementAfterLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';

import {FALLBACK_COLUMN_NODE_HEIGHT} from './ProcedureRunDesignFlowchartColumnNode';
import {
  getMaxMeasuredNodeHeight,
  recomputeColumnPositions,
  recomputeGridPositions,
} from './ProcedureRunDesignFlowchartLayoutUtils';
import type {
  ProcedureRunDesignFlowchartLayoutNode,
  ProcedureRunDesignFlowchartLayoutOptions,
} from './useProcedureRunDesignFlowchartLayout';

interface UseProcedureRunDesignFlowchartMeasuredLayoutArgs {
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>;
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions;
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  setNodes: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutNode>>>;
}

/**
 * Drives the second phase of the two-phase layout: once ReactFlow has measured every expected
 * node, recompute grid positions and unhide the nodes. Until then the current nodes are left
 * untouched so the visible flowchart isn't disturbed.
 */
export function useProcedureRunDesignFlowchartMeasuredLayout({
  initialColumnNodes,
  initialRows,
  layoutOptions,
  nodes,
  setNodes,
}: UseProcedureRunDesignFlowchartMeasuredLayoutArgs): void {
  const nodesInitialized = useNodesInitialized();

  // Hash id + dimensions only — position-only changes produce the same hash,
  // preventing setNodes -> hash-change -> re-layout loops
  const nodeLayoutDepsHash = getNodeLayoutDepsHash(nodes);

  useLayoutEffect(() => {
    if (!nodesInitialized) {
      return;
    }

    const expectedIds = new Set([
      ...initialColumnNodes.map((node) => node.id),
      ...initialRows.flat().map((node) => node.id),
    ]);

    setNodes((currentNodes) => {
      const measuredNodesById = new Map(currentNodes.map((currentNode) => [currentNode.id, currentNode]));

      // Lay out only once every expected node has been measured. Until then, leave the current
      // nodes untouched (see the early return below) and wait for the next measurement.
      const hasLayout = [...expectedIds].every((id) => {
        const measuredNode = measuredNodesById.get(id);
        return measuredNode != null && (measuredNode.width ?? 0) > 0 && (measuredNode.height ?? 0) > 0;
      });

      if (!hasLayout) {
        // Some expected nodes are not yet measured (e.g. a newly-added replicate node).
        // Don't disturb existing visible nodes — leave state as-is and wait for the next
        // nodeLayoutDepsHash change.
        return currentNodes;
      }
      // The hasLayout guard above guarantees this entry exists and is measured.
      const recomputedNodes = recomputeNodePositions(
        initialRows,
        initialColumnNodes,
        measuredNodesById,
        layoutOptions
      );
      return recomputedNodes.map((node) => unhideFlowchartElementAfterLayout(node));
    });
    // nodeLayoutDepsHash captures dimension changes without including positions, so layout only
    // re-runs when sizes change, not when setNodes updates positions. layoutOptions is expected
    // to be a stable module-level constant at the call site.
  }, [nodesInitialized, nodeLayoutDepsHash, initialRows, initialColumnNodes, setNodes, layoutOptions]);
}

function getNodeLayoutDepsHash(nodes: Array<ProcedureRunDesignFlowchartLayoutNode>): string {
  return JSON.stringify(nodes.map(({id, height, width}) => ({id, height, width})));
}

/**
 * Recomputes the positions of the nodes in the flowchart.
 * This function is called when all expected nodes are measured.
 * It recomputes the positions of the nodes in the flowchart and returns the recomputed nodes.
 */
function recomputeNodePositions(
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>,
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  measuredNodesById: Map<string, ProcedureRunDesignFlowchartLayoutNode>,
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions
): Array<ProcedureRunDesignFlowchartLayoutNode> {
  const measuredColumnNodes = initialColumnNodes.map((columnNode) => {
    const {width, height} = measuredNodesById.get(columnNode.id) ?? columnNode;
    return {...columnNode, width, height};
  });

  const measuredRows = initialRows.map((row) =>
    row.map((rowNode) => {
      // The hasLayout guard above guarantees this entry exists and is measured.
      const {width, height} = measuredNodesById.get(rowNode.id) ?? rowNode;
      return {...rowNode, width, height};
    })
  );

  // Grid rows start below the column headers
  const columnHeaderHeight = getMaxMeasuredNodeHeight(measuredColumnNodes, FALLBACK_COLUMN_NODE_HEIGHT);

  const {repositionedRows, columnDimensions} = recomputeGridPositions(measuredRows, {
    horizontalGap: layoutOptions.horizontalGap,
    verticalGap: layoutOptions.verticalGap,
    startX: layoutOptions.startX,
    startY: layoutOptions.startY + columnHeaderHeight + layoutOptions.columnHeaderBottomGap,
  });

  const repositionedColumnNodes = recomputeColumnPositions(measuredColumnNodes, columnDimensions, {
    startY: layoutOptions.startY,
  });

  return [...repositionedColumnNodes, ...repositionedRows.flat()];
}
